import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import CreateConfirm from "./CreateConfirm";

export const ChangeUsernameModal = ({ isOpen, onClose, userData, dispatch, changeUsername, GET_Company_Alter_SUCCESS }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [showQuitConfirm, setShowQuitConfirm] = useState(false);
  
  const currentPasswordRef = useRef(null);
  const newUsernameRef = useRef(null);
  const lastFocusedFieldRef = useRef(null);
  
  const [focusedField, setFocusedField] = useState("currentPassword");

  useEffect(() => {
    if (isOpen) {
      setCurrentPassword("");
      setNewUsername("");
      setFocusedField("currentPassword");
      setShowQuitConfirm(false);
      setTimeout(() => {
        currentPasswordRef.current?.focus();
        currentPasswordRef.current?.select();
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (showQuitConfirm) return;
    
    if (!currentPassword.trim()) {
      toast.error("Current password is required", { position: toast.POSITION.TOP_CENTER });
      currentPasswordRef.current?.focus();
      return;
    }
    if (!newUsername.trim()) {
      toast.error("New username is required", { position: toast.POSITION.TOP_CENTER });
      newUsernameRef.current?.focus();
      return;
    }
    
    const currentName = userData?.user_name || userData?.username || "";
    if (newUsername.trim() === currentName) {
      toast.error("New username must be different from current username", { position: toast.POSITION.TOP_CENTER });
      newUsernameRef.current?.focus();
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("user_id", userData?.id || "");
    formData.append("login_type", userData?.login_type || "company");
    formData.append("new_username", newUsername.trim());
    formData.append("current_password", currentPassword);

    dispatch(changeUsername(formData))
      .then((res) => {
        setLoading(false);
        if (res?.status) {
          toast.success(res?.message || "Username changed successfully", { position: toast.POSITION.TOP_CENTER });
          
          // Update Redux state immediately
          dispatch({
            type: GET_Company_Alter_SUCCESS,
            payload: {
              data: {
                ...userData,
                user_name: newUsername.trim(),
                username: newUsername.trim()
              }
            }
          });
          onClose();
        } else {
          toast.error(res?.message || "Failed to change username", { position: toast.POSITION.TOP_CENTER });
        }
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err?.message || "An error occurred", { position: toast.POSITION.TOP_CENTER });
      });
  };

  const handleCancelClick = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    lastFocusedFieldRef.current = document.activeElement;
    setShowQuitConfirm(true);
  };

  const handleKeyDown = (e) => {
    if (showQuitConfirm) {
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      lastFocusedFieldRef.current = document.activeElement;
      setShowQuitConfirm(true);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (document.activeElement === currentPasswordRef.current) {
        newUsernameRef.current?.focus();
        setFocusedField("newUsername");
      } else if (document.activeElement === newUsernameRef.current) {
        handleSubmit();
      }
    }
  };

  const isFirefox = typeof window !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

  return (
    <div
      className="credentials-change-modal"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        zIndex: 1500,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'sans-serif'
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          width: '400px',
          border: '2px solid #5d5d5d',
          background: '#f1f1f1',
          padding: '0px',
          boxShadow: '0px 4px 15px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{ background: '#006699', color: '#fff', padding: '8px 12px', fontSize: '14px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Change Username</span>
          <span style={{ cursor: 'pointer', fontSize: '20px', lineHeight: '1' }} onClick={handleCancelClick}>&times;</span>
        </div>
        
        {/* Form Body */}
        <div style={{ padding: '20px 15px' }}>
          <div className="mb-3 d-flex align-items-center" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <div style={{ width: '40%', textAlign: 'left', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>Current Username</div>
            <div style={{ width: '5%', textAlign: 'center', color: '#333' }}>:</div>
            <div style={{ width: '55%' }}>
              <input
                type="text"
                disabled
                value={userData?.user_name || userData?.username || ""}
                style={{ width: '100%', fontSize: '13px', padding: '3px 6px', background: '#e0e0e0', border: '1px solid #ccc', outline: 'none', color: '#666' }}
              />
            </div>
          </div>

          <div className="mb-3 d-flex align-items-center" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <div style={{ width: '40%', textAlign: 'left', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>Current Password</div>
            <div style={{ width: '5%', textAlign: 'center', color: '#333' }}>:</div>
            <div style={{ width: '55%' }}>
              <input
                ref={currentPasswordRef}
                type={isFirefox ? "password" : "text"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                onFocus={() => setFocusedField("currentPassword")}
                style={{
                  width: '100%',
                  fontSize: '13px',
                  padding: '3px 6px',
                  border: '1px solid #7f9db9',
                  outline: 'none',
                  background: focusedField === "currentPassword" ? "#ffffe0" : "#fff",
                  ...(isFirefox ? {} : { WebkitTextSecurity: 'disc', textSecurity: 'disc' })
                }}
                autoComplete="one-time-code"
              />
            </div>
          </div>

          <div className="mb-3 d-flex align-items-center" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <div style={{ width: '40%', textAlign: 'left', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>New Username</div>
            <div style={{ width: '5%', textAlign: 'center', color: '#333' }}>:</div>
            <div style={{ width: '55%' }}>
              <input
                ref={newUsernameRef}
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                onFocus={() => setFocusedField("newUsername")}
                style={{
                  width: '100%',
                  fontSize: '13px',
                  padding: '3px 6px',
                  border: '1px solid #7f9db9',
                  outline: 'none',
                  background: focusedField === "newUsername" ? "#ffffe0" : "#fff"
                }}
                autoComplete="one-time-code"
              />
            </div>
          </div>
        </div>

        {/* Footer buttons */}
        <div style={{ background: '#e0e0e0', padding: '10px 15px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #ccc' }}>
          <button
            onClick={handleCancelClick}
            style={{
              padding: '4px 15px',
              fontSize: '13px',
              background: '#f5f5f5',
              border: '1px solid #777',
              marginRight: '10px',
              cursor: 'pointer'
            }}
          >
            Cancel (Esc)
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: '4px 15px',
              fontSize: '13px',
              background: '#006699',
              color: '#fff',
              border: '1px solid #004466',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {loading ? "Saving..." : "Accept (Enter)"}
          </button>
        </div>
      </div>
      {showQuitConfirm && (
        <CreateConfirm
          message="Quit"
          handleSubmit={(e) => {
            if (e && e.preventDefault) e.preventDefault();
            setShowQuitConfirm(false);
            onClose();
          }}
          handleFocusReset={(e) => {
            if (e && e.preventDefault) e.preventDefault();
            setShowQuitConfirm(false);
            setTimeout(() => {
              if (lastFocusedFieldRef.current) {
                lastFocusedFieldRef.current.focus();
              } else {
                currentPasswordRef.current?.focus();
              }
            }, 50);
          }}
        />
      )}
    </div>
  );
};

export const ChangePasswordModal = ({ isOpen, onClose, userData, dispatch, changePassword }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showQuitConfirm, setShowQuitConfirm] = useState(false);

  const currentPasswordRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const lastFocusedFieldRef = useRef(null);

  const [focusedField, setFocusedField] = useState("currentPassword");

  useEffect(() => {
    if (isOpen) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setFocusedField("currentPassword");
      setShowQuitConfirm(false);
      setTimeout(() => {
        currentPasswordRef.current?.focus();
        currentPasswordRef.current?.select();
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (showQuitConfirm) return;

    if (!currentPassword.trim()) {
      toast.error("Current password is required", { position: toast.POSITION.TOP_CENTER });
      currentPasswordRef.current?.focus();
      return;
    }
    if (!newPassword.trim()) {
      toast.error("New password is required", { position: toast.POSITION.TOP_CENTER });
      newPasswordRef.current?.focus();
      return;
    }
    if (newPassword.trim().length < 4) {
      toast.error("Password must be at least 4 characters long", { position: toast.POSITION.TOP_CENTER });
      newPasswordRef.current?.focus();
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match", { position: toast.POSITION.TOP_CENTER });
      confirmPasswordRef.current?.focus();
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("user_id", userData?.id || "");
    formData.append("login_type", userData?.login_type || "company");
    formData.append("current_password", currentPassword);
    formData.append("new_password", newPassword);

    dispatch(changePassword(formData))
      .then((res) => {
        setLoading(false);
        if (res?.status) {
          toast.success(res?.message || "Password changed successfully", { position: toast.POSITION.TOP_CENTER });
          onClose();
        } else {
          toast.error(res?.message || "Failed to change password", { position: toast.POSITION.TOP_CENTER });
        }
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err?.message || "An error occurred", { position: toast.POSITION.TOP_CENTER });
      });
  };

  const handleCancelClick = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    lastFocusedFieldRef.current = document.activeElement;
    setShowQuitConfirm(true);
  };

  const handleKeyDown = (e) => {
    if (showQuitConfirm) {
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      lastFocusedFieldRef.current = document.activeElement;
      setShowQuitConfirm(true);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (document.activeElement === currentPasswordRef.current) {
        newPasswordRef.current?.focus();
        setFocusedField("newPassword");
      } else if (document.activeElement === newPasswordRef.current) {
        confirmPasswordRef.current?.focus();
        setFocusedField("confirmPassword");
      } else if (document.activeElement === confirmPasswordRef.current) {
        handleSubmit();
      }
    }
  };

  const isFirefox = typeof window !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

  return (
    <div
      className="credentials-change-modal"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        zIndex: 1500,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'sans-serif'
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          width: '400px',
          border: '2px solid #5d5d5d',
          background: '#f1f1f1',
          padding: '0px',
          boxShadow: '0px 4px 15px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{ background: '#006699', color: '#fff', padding: '8px 12px', fontSize: '14px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Change Password</span>
          <span style={{ cursor: 'pointer', fontSize: '20px', lineHeight: '1' }} onClick={handleCancelClick}>&times;</span>
        </div>

        {/* Form Body */}
        <div style={{ padding: '20px 15px' }}>
          <div className="mb-3 d-flex align-items-center" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <div style={{ width: '40%', textAlign: 'left', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>Current Password</div>
            <div style={{ width: '5%', textAlign: 'center', color: '#333' }}>:</div>
            <div style={{ width: '55%' }}>
              <input
                ref={currentPasswordRef}
                type={isFirefox ? "password" : "text"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                onFocus={() => setFocusedField("currentPassword")}
                style={{
                  width: '100%',
                  fontSize: '13px',
                  padding: '3px 6px',
                  border: '1px solid #7f9db9',
                  outline: 'none',
                  background: focusedField === "currentPassword" ? "#ffffe0" : "#fff",
                  ...(isFirefox ? {} : { WebkitTextSecurity: 'disc', textSecurity: 'disc' })
                }}
                autoComplete="one-time-code"
              />
            </div>
          </div>

          <div className="mb-3 d-flex align-items-center" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <div style={{ width: '40%', textAlign: 'left', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>New Password</div>
            <div style={{ width: '5%', textAlign: 'center', color: '#333' }}>:</div>
            <div style={{ width: '55%' }}>
              <input
                ref={newPasswordRef}
                type={isFirefox ? "password" : "text"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onFocus={() => setFocusedField("newPassword")}
                style={{
                  width: '100%',
                  fontSize: '13px',
                  padding: '3px 6px',
                  border: '1px solid #7f9db9',
                  outline: 'none',
                  background: focusedField === "newPassword" ? "#ffffe0" : "#fff",
                  ...(isFirefox ? {} : { WebkitTextSecurity: 'disc', textSecurity: 'disc' })
                }}
                autoComplete="one-time-code"
              />
            </div>
          </div>

          <div className="mb-3 d-flex align-items-center" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <div style={{ width: '40%', textAlign: 'left', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>Confirm New Password</div>
            <div style={{ width: '5%', textAlign: 'center', color: '#333' }}>:</div>
            <div style={{ width: '55%' }}>
              <input
                ref={confirmPasswordRef}
                type={isFirefox ? "password" : "text"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setFocusedField("confirmPassword")}
                style={{
                  width: '100%',
                  fontSize: '13px',
                  padding: '3px 6px',
                  border: '1px solid #7f9db9',
                  outline: 'none',
                  background: focusedField === "confirmPassword" ? "#ffffe0" : "#fff",
                  ...(isFirefox ? {} : { WebkitTextSecurity: 'disc', textSecurity: 'disc' })
                }}
                autoComplete="one-time-code"
              />
            </div>
          </div>
        </div>

        {/* Footer buttons */}
        <div style={{ background: '#e0e0e0', padding: '10px 15px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #ccc' }}>
          <button
            onClick={handleCancelClick}
            style={{
              padding: '4px 15px',
              fontSize: '13px',
              background: '#f5f5f5',
              border: '1px solid #777',
              marginRight: '10px',
              cursor: 'pointer'
            }}
          >
            Cancel (Esc)
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: '4px 15px',
              fontSize: '13px',
              background: '#006699',
              color: '#fff',
              border: '1px solid #004466',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {loading ? "Saving..." : "Accept (Enter)"}
          </button>
        </div>
      </div>
      {showQuitConfirm && (
        <CreateConfirm
          message="Quit"
          handleSubmit={(e) => {
            if (e && e.preventDefault) e.preventDefault();
            setShowQuitConfirm(false);
            onClose();
          }}
          handleFocusReset={(e) => {
            if (e && e.preventDefault) e.preventDefault();
            setShowQuitConfirm(false);
            setTimeout(() => {
              if (lastFocusedFieldRef.current) {
                lastFocusedFieldRef.current.focus();
              } else {
                currentPasswordRef.current?.focus();
              }
            }, 50);
          }}
        />
      )}
    </div>
  );
};
